import React, { useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import { Section } from './education/Section';
import {
  calculatePipelineScenario, GENERATION_CASES, REFERENCE_SCENARIO, type ThroughputInputs,
} from '../lib/pipeline-throughput-model';

interface PipelineScenarioProps {
  recordedDiameter: number | null;
}

const ADVANCED_INPUTS: { key: keyof ThroughputInputs; label: string }[] = [
  { key: 'temperatureF', label: 'Flowing gas temperature (°F)' },
  { key: 'specificGravity', label: 'Gas relative density (air = 1)' },
  { key: 'compressibility', label: 'Flowing compressibility (Z)' },
  { key: 'roughnessMm', label: 'Inside roughness (mm)' },
  { key: 'viscosityPaS', label: 'Dynamic viscosity (Pa·s)' },
  { key: 'atmosphericPsia', label: 'Atmospheric pressure (psia)' },
  { key: 'basePressurePsia', label: 'Standard pressure (psia)' },
  { key: 'baseTemperatureF', label: 'Standard temperature (°F)' },
  { key: 'baseCompressibility', label: 'Standard compressibility (Z)' },
  { key: 'hhvBtuScf', label: 'Gas heating value, HHV (Btu/scf)' },
  { key: 'heatRateBtuKwh', label: 'Net HHV heat rate (Btu/kWh)' },
];

/** Read-time scenario; its inputs do not change stored pipeline facts or rankings. */
export const PipelineScenario: React.FC<PipelineScenarioProps> = ({ recordedDiameter }) => {
  const { theme } = useTheme();
  const initialInputs = (): ThroughputInputs => ({
    ...REFERENCE_SCENARIO.inputs,
    diameterInches: recordedDiameter != null && Number(recordedDiameter) > 0
      ? Number(recordedDiameter) : Number.NaN,
  });
  const [inputs, setInputs] = useState<ThroughputInputs>(initialInputs);
  const [edited, setEdited] = useState(false);
  const result = calculatePipelineScenario(inputs);
  const generationCase = GENERATION_CASES.find(item => item.heatRateBtuKwh === inputs.heatRateBtuKwh);
  const inputStyle: React.CSSProperties = {
    width: '100%', minWidth: 0, boxSizing: 'border-box', padding: '7px 8px',
    color: theme.text, background: theme.background, border: `1px solid ${theme.border}`,
    borderRadius: 4, fontSize: 13,
  };
  const noteStyle: React.CSSProperties = { fontSize: 12, lineHeight: 1.5, color: theme.textSecondary };
  const change = (key: keyof ThroughputInputs, value: number) => {
    setInputs(previous => ({ ...previous, [key]: value }));
    setEdited(true);
  };
  const field = (key: keyof ThroughputInputs, label: string) => (
    <label key={key} style={{ display: 'grid', gap: 4, fontSize: 12, color: theme.textSecondary }}>
      {label}
      <input type="number" step="any" aria-label={label} style={inputStyle}
        value={Number.isFinite(inputs[key]) ? inputs[key] : ''}
        onChange={event => change(key, event.currentTarget.valueAsNumber)} />
    </label>
  );
  const format = (value: number, digits = 0) => Number.isFinite(value)
    ? value.toLocaleString('en-US', value > 0 && value < 1
      ? { maximumSignificantDigits: 3 } : { maximumFractionDigits: digits })
    : '—';

  return (
    <Section title="Modeled gas & power">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <strong style={{ fontSize: 14, color: theme.text }}>{edited ? 'Custom scenario' : 'Reference scenario'}</strong>
        {edited && <button type="button" onClick={() => { setInputs(initialInputs()); setEdited(false); }}
          style={{ color: theme.link, border: 0, background: 'none', cursor: 'pointer', fontSize: 12 }}>Reset</button>}
      </div>
      <p style={{ ...noteStyle, margin: '6px 0 12px' }}>
        Delivery is unverified. This natural-gas estimate assumes the conditions below; it does not establish spare capacity or service at a site.
      </p>
      {result.ok ? (
        <div style={{ border: `1px solid ${theme.border}`, borderRadius: 6, padding: '8px 12px', marginBottom: 12 }}>
          {[
            ['gas-volume', 'Modeled gas', `${format(result.gasMmscfd, 1)} MMscf/day`],
            ['gas-energy', 'Gas energy', `${format(result.gasDthDay)} Dth/day`],
            ['net-mw', 'Continuous net MW', `${format(result.netMw)} MW`],
          ].map(([key, label, value]) => (
            <div key={key} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '5px 0', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, color: theme.textSecondary }}>{label}</span>
              <strong data-scenario-output={key} style={{ fontSize: key === 'net-mw' ? 20 : 14, color: theme.text }}>{value}</strong>
            </div>
          ))}
          <div style={{ ...noteStyle, marginTop: 5 }}>Electrical equivalent if this gas flow is sustained at the selected heat rate.</div>
        </div>
      ) : (
        <p role="status" style={noteStyle}>{!Number.isFinite(inputs.diameterInches)
          ? 'Enter an inside diameter to calculate a scenario.' : result.error}</p>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
        {field('diameterInches', 'Assumed inside diameter (in)')}
        {field('lengthMiles', 'Hydraulic reach (mi)')}
        {field('inletPsig', 'Inlet pressure (psig)')}
        {field('outletPsig', 'Outlet pressure (psig)')}
      </div>
      <p style={{ ...noteStyle, margin: '8px 0 12px' }}>
        Initially, the recorded diameter is used as assumed inside diameter. Pressure and reach are reference assumptions, not recorded operating conditions. Reach means the flow path between pressure boundaries, not the length of a map segment.
      </p>
      <label style={{ display: 'grid', gap: 5, fontSize: 12, color: theme.textSecondary }}>
        Generation case
        <select aria-label="Generation case" style={inputStyle} value={generationCase?.id || 'custom'}
          onChange={event => {
            const selected = GENERATION_CASES.find(item => item.id === event.target.value);
            if (selected) change('heatRateBtuKwh', selected.heatRateBtuKwh);
          }}>
          {GENERATION_CASES.map(item => <option key={item.id} value={item.id}>{item.label}</option>)}
          {!generationCase && <option value="custom">Custom net HHV heat rate</option>}
        </select>
      </label>
      <p style={{ ...noteStyle, margin: '6px 0 12px' }}>
        {format(inputs.heatRateBtuKwh)} Btu/kWh net HHV · gas HHV {format(inputs.hhvBtuScf)} Btu/scf.
        {generationCase && <> <a href={generationCase.sourceUrl} target="_blank" rel="noreferrer" style={{ color: theme.link }}>NETL design case</a>.</>}
        {' '}Design efficiency varies with equipment, load and conditions.
      </p>
      <details>
        <summary style={{ cursor: 'pointer', color: theme.link, fontSize: 12 }}>All assumptions & calculations</summary>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10, marginTop: 12 }}>
          {ADVANCED_INPUTS.map(({ key, label }) => field(key, label))}
        </div>
        <div style={noteStyle}>
          <p>Standard volume uses {format(inputs.baseTemperatureF, 2)}°F, {format(inputs.basePressurePsia, 2)} psia and Z = {format(inputs.baseCompressibility, 3)}. One MMscf/day is one million standard cubic feet per day. One Dth is one MMBtu.</p>
          <p>Steady, isothermal, horizontal Darcy–Weisbach with Colebrook friction:</p>
          <div style={{ overflowWrap: 'anywhere', fontFamily: 'monospace', fontSize: 11 }}>
            A = πD²/4<br />
            ṁ = A√[D(p₁² − p₂²)/(f Z Rₛ T L)]<br />
            Re = 4ṁ/(πDμ)<br />
            1/√f = −2 log₁₀[ε/(3.7D) + 2.51/(Re√f)]<br />
            ρₛ = pₛ/(Zₛ Rₛ Tₛ); Qₛ = ṁ/ρₛ<br />
            Dth/day = MMscf/day × HHV<br />
            net MW = Dth/day × 1,000 / (24 × net HHV heat rate)
          </div>
          <p>The hydraulic calculation uses metres, pascals absolute, kelvin and kg/s. Gauge pressure is converted using the atmospheric pressure above. D is inside diameter (outside diameter minus twice the wall thickness, when known), L is reach, f is the Darcy friction factor, ε is roughness and μ is viscosity. Rₛ uses relative density and dry-air molar mass 28.96546 kg/kmol. For laminar flow, f = 64/Re.</p>
          <p>No utilization or headroom factor is applied. Elevation, network withdrawals, compression and changing gas properties are outside this single-pipe model. Z is assumed. A fractional plant equivalent does not establish a feasible plant design.</p>
          <p><a href="https://www.eia.gov/tools/faqs/faq.php?id=45" target="_blank" rel="noreferrer" style={{ color: theme.link }}>EIA heating-value reference</a>{' · '}
            <a href="https://www.osti.gov/servlets/purl/2447960" target="_blank" rel="noreferrer" style={{ color: theme.link }}>Gas-flow equations</a></p>
          <p>Reference version: {REFERENCE_SCENARIO.version}. Changing these inputs explores a scenario; it does not update site rankings.</p>
        </div>
      </details>
      {result.ok && result.warnings.map(warning => <p key={warning} role="status" style={noteStyle}>{warning}</p>)}
    </Section>
  );
};
